import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Banknote,
  Handshake,
  BarChart3,
  Compass,
  Briefcase,
  MapPin,
  Mail,
  Menu,
  X,
  ChevronDown,
  Linkedin,
} from "lucide-react";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Team", href: "#team" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-effect" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
        <a href="#" data-testid="logo-link">
          <img
  src="/WhiteLogo.png"
  alt="Alvron Partners"
  style={{
    height: "40px",
    transform: "translateY(5px)",
  }}
  className="w-auto"
  data-testid="logo"
/>

        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-12">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="nav-link text-sm tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`nav-${link.name.toLowerCase()}`}
            >
              {link.name}
            </a>
          ))}
        </div>

        <a
          href="#contact"
          className="hidden md:block btn-primary"
          data-testid="nav-contact-btn"
        >
          Get in Touch
        </a>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden glass-effect absolute top-20 left-0 right-0 py-8 px-6"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-lg text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contact"
              className="btn-primary text-center mt-4"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-contact-btn"
            >
              Get in Touch
            </a>
          </div>
        </motion.div>
      )}
    </header>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1649524858012-803e972ca07e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY5ODAwMjE3fDA&ixlib=rb-4.1.0&q=85")',
        }}
      />

      {/* Glow Effect */}
      <div className="absolute inset-0 hero-glow" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="space-y-8"
        >
          <motion.p
            variants={fadeUp}
            className="text-xs uppercase tracking-[0.3em] text-emerald-500 font-semibold"
          >
            WHERE CAPITAL MEETS CLARITY
          </motion.p>

          <motion.h1
            variants={fadeUp}
            className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9]"
            data-testid="hero-title"
          >
            Excelling Investments with
            <br />
            <span className="text-emerald-500">Creativity & Innovation</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
            data-testid="hero-subtitle"
          >
            A boutique firm with products tailored to your financial needs
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            <a href="#services" className="btn-primary" data-testid="hero-services-btn">
              Our Expertise
            </a>
            <a href="#contact" className="btn-outline" data-testid="hero-contact-btn">
              Book Consultation
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <a href="#about" className="flex flex-col items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <span className="text-xs uppercase tracking-widest">Scroll</span>
            <ChevronDown size={20} className="animate-bounce" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid md:grid-cols-2 gap-16 items-center"
        >
          <motion.div variants={fadeUp} className="space-y-8">
            <p className="text-xs uppercase tracking-[0.3em] text-emerald-500 font-semibold">
              Who We Are
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight" data-testid="about-title">
              About Us
            </h2>
            <p className="text-muted-foreground leading-relaxed text-lg" data-testid="about-description">
              <strong className="text-foreground">Alvron Partners</strong> is a boutique investment banking firm that specializes in raising capital for financial institutions. As a part of its advisory services, it assists its clients by providing wealth and investment management consultancy.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Our firm assists its clients to achieve superior results through innovative and integrated financial advisory services. Our dedicated and experienced team supports clients on their most crucial decisions including mergers & acquisitions, recapitalizations, strategic shareholder advisory, and other corporate finance matters.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Founded by former investment bankers at <strong className="text-foreground">J.P Morgan</strong> and <strong className="text-foreground">Goldman Sachs</strong>, the firm is headquartered in Mumbai.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="relative">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1769069918427-86dad80a5bcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG1lZXRpbmclMjBzaWxob3VldHRlfGVufDB8fHx8MTc2OTgwMDIyMXww&ixlib=rb-4.1.0&q=85"
                alt="Business Advisory"
                className="w-full h-full object-cover"
                data-testid="about-image"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-emerald-500 p-8 hidden md:block">
              <p className="text-4xl font-serif font-light text-white">10+</p>
              <p className="text-xs uppercase tracking-widest text-white/80 mt-1">Years Experience</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const servicesData = [
  {
    title: "Credit Solutions",
    description:
      "Customized solutions to meet your credit requirements while minimizing credit and price risk. We act as an arranger facility between lender and borrower, structuring win-win transactions.",
    icon: Banknote,
    span: "md:col-span-2",
  },
  {
    title: "Mergers & Acquisitions",
    description:
      "Global reach for both sell side and buy side transactions across consumer services, FIG, digital technology and pharmaceuticals.",
    icon: Handshake,
    span: "md:col-span-1",
  },
  {
    title: "Valuations",
    description:
      "Fair value assessment of any asset class through comprehensive analysis of business dynamics, industry trends and value drivers.",
    icon: BarChart3,
    span: "md:col-span-1",
  },
  {
    title: "Strategic Advisory Services",
    description:
      "Expert guidance to analyze, evaluate and make informed decisions. Our established track-record enables clients to understand options to grow, exit or reposition their business.",
    icon: Compass,
    span: "md:col-span-2",
  },
  {
    title: "Asset Management",
    description:
      "Investment strategies spanning equity, fixed income, money market, ETFs and more. Focus on consistent and sustainable risk-adjusted returns with capital growth and preservation.",
    icon: Briefcase,
    span: "md:col-span-3",
  },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-card"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeUp}
            className="text-xs uppercase tracking-[0.3em] text-emerald-500 font-semibold mb-4"
          >
            What We Do
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-serif text-4xl md:text-5xl font-light tracking-tight"
            data-testid="services-title"
          >
            Our Services
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground mt-6 max-w-2xl mx-auto">
            We craft customized financial solutions in the below mentioned domains to entrepreneurs and businesses
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {servicesData.map((service, index) => (
            <motion.div
              key={service.title}
              variants={fadeUp}
              className={`service-card bg-background border border-white/5 p-8 md:p-10 ${service.span}`}
              data-testid={`service-card-${index}`}
            >
              <service.icon className="w-10 h-10 text-emerald-500 mb-6" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl mb-4" data-testid={`service-title-${index}`}>
                {service.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mt-16"
        >
          <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
            Our key industry intelligence and customer-centric approach creates a trusting environment for clients and team
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Team Section
const teamData = [
  {
    name: "Alok Gupta",
    role: "Founder",
    linkedin: "https://www.linkedin.com/in/alok-gupta-39618a231",
    image:
      "/alok.png",
    bio: "Founded Alvron after a decade of experience in financial services sector. Prior to Alvron, served as VP at Goldman Sachs. Holds MBA from IMT Ghaziabad and BTech from Harcourt Butler Technical University.",
  },
  {
    name: "Riddhima Agarwal",
    role: "M&A Lead",
    linkedin: "https://www.linkedin.com/in/riddhima-agarwal-a85ab0235",
    image:
      "/riddhima.png",
    bio: "Joined Alvron in 2021 after 5 years with M&A Advisory team at Deloitte. Brings deep knowledge of consumer services, digital technology sectors and now co-leads Alvron's M&A initiatives.",
  },
  {
    name: "Manas Mittal",
    role: "Credit Solutions Head",
    linkedin: "https://www.linkedin.com/in/manas-mittal-3b8ab1235",
    image: "/manas.png",
    bio: "Brings more than 8 years of experience in leading and executing credit solutions business. Previously worked as structured finance engineer with Northern Arc Capital. Holds MBA from IIM Indore and BCom from Hansraj College.",
  },
];

const Team = () => {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeUp}
            className="text-xs uppercase tracking-[0.3em] text-emerald-500 font-semibold mb-4"
          >
            Our People
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-serif text-4xl md:text-5xl font-light tracking-tight"
            data-testid="team-title"
          >
            Leadership Team
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {teamData.map((member, index) => (
            <motion.div
              key={member.name}
              variants={fadeUp}
              className="team-card group"
              data-testid={`team-card-${index}`}
            >
              <div className="aspect-[3/4] overflow-hidden mb-6">
                <img
                  src={member.image}
                  alt={member.name}
                  className="team-image w-full h-full object-cover"
                  data-testid={`team-image-${index}`}
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-serif text-2xl" data-testid={`team-name-${index}`}>
                      {member.name}
                    </h3>
                    <p className="text-emerald-500 text-sm uppercase tracking-wider">
                      {member.role}
                    </p>
                  </div>
                  <a
  href={member.linkedin}
  target="_blank"
  rel="noopener noreferrer"
  className="p-2 border border-white/10 hover:border-emerald-500 hover:text-emerald-500 transition-colors"
  data-testid={`team-linkedin-${index}`}
>
  <Linkedin size={18} />
</a>

                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">{member.bio}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const response = await fetch("https://formspree.io/f/mojldlnd", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        ...formData,
        _subject: "New Contact Form Submission – Alvron Partners",
        _replyto: formData.email,
      }),
    });

    if (response.ok) {
      toast.success("Thank you for reaching out! We'll get back to you soon.", {
        duration: 5000,
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } else {
      toast.error("Something went wrong. Please try again.");
    }
  } catch (error) {
    toast.error("Network error. Please try again later.");
  }
};

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1643756173714-5b492591768c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtdW1iYWklMjBza3lsaW5lJTIwbmlnaHR8ZW58MHx8fHwxNzY5ODAwMjE0fDA&ixlib=rb-4.1.0&q=85")',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid md:grid-cols-2 gap-16"
        >
          <motion.div variants={fadeUp} className="space-y-8">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-500 font-semibold mb-4">
                Get in Touch
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight" data-testid="contact-title">
                Contact Us
              </h2>
            </div>

            <p className="text-muted-foreground text-lg leading-relaxed">
              We're ready to help you achieve your financial goals. Reach out to schedule a consultation.
            </p>

            <div className="space-y-6 pt-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30">
                  <MapPin className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Our Office</h4>
                  <p className="text-muted-foreground" data-testid="contact-address">
                    18, Rajan Pada Mindspace<br />
                    Malad West, Mumbai, India
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30">
                  <Mail className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Email Us</h4>
                  <a
                    href="mailto:contact@alvronpartners.com"
                    className="text-muted-foreground hover:text-emerald-500 transition-colors"
                    data-testid="contact-email"
                  >
                    contact@alvronpartners.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeUp}>
            <form
              onSubmit={handleSubmit}
              className="contact-form glass-effect p-8 md:p-10 space-y-6"
              data-testid="contact-form"
            >
              <input type="text" name="_gotcha" className="hidden" />
              <h3 className="font-serif text-2xl mb-2">We're Ready, Let's Talk.</h3>
              <p className="text-muted-foreground text-sm mb-6">Book a business consultation</p>

              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="contact-input-name"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="contact-input-email"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Your Phone"
                value={formData.phone}
                onChange={handleChange}
                data-testid="contact-input-phone"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                required
                data-testid="contact-input-message"
              />
              <button type="submit" className="btn-primary w-full" data-testid="contact-submit-btn">
                Submit
              </button>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-card py-16 px-6 md:px-12 lg:px-24 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2 space-y-6">
            <img
  src="/WhiteLogo.png"
  alt="Alvron Partners"
  style={{
    height: "50px",
    transform: "translateX(-8px)",
  }}
  className="w-auto"
  data-testid="footer-logo"
/>

            <p className="text-muted-foreground max-w-md">
              A boutique investment banking firm specializing in raising capital for financial institutions with wealth and investment management consultancy.
            </p>
            <a
  href="https://www.linkedin.com/company/alvron-partners/"
  target="_blank"
  rel="noopener noreferrer"
  className="inline-flex items-center gap-2 text-muted-foreground hover:text-emerald-500 transition-colors"
  aria-label="Alvron Partners LinkedIn"
>
  <span className="p-2 border border-white/10 hover:border-emerald-500 transition-colors">
    <Linkedin size={18} />
  </span>
</a>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Quick Links</h4>
            <div className="space-y-3">
              <a href="#about" className="block text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-link-about">About</a>
              <a href="#services" className="block text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-link-services">Services</a>
              <a href="#team" className="block text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-link-team">Team</a>
              <a href="#contact" className="block text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-link-contact">Contact</a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Legal</h4>
            <div className="space-y-3">
              <Link to="/privacy-policy" className="block text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="block text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-link-terms">Terms & Conditions</Link>
            </div>
          </div>
        </div>

        <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm" data-testid="footer-copyright">
            © {new Date().getFullYear()} Alvron Partners. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navigation />
      <main>
        <Hero />
        <About />
        <Services />
        <Team />
        <Contact />
      </main>
      <Footer />
    </>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <div className="App bg-background min-h-screen" data-testid="app">
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#111412",
              color: "#E2E8E5",
              border: "1px solid rgba(255,255,255,0.05)",
            },
          }}
        />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
